<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  
  // Props
  export let options = []; // Array of objects with value and label properties
  export let value = '';   // Selected value (bound)
  export let placeholder = $_('searchable_select.placeholder');
  export let id = '';
  
  // Internal state
  let isOpen = false;
  let searchText = '';
  let filteredOptions = [...options];
  
  const dispatch = createEventDispatcher();
  
  // Get the image for the currently selected value
  $: selectedImage = options.find(opt => String(opt.value) === String(value))?.image;

  // Get the label for the currently selected value
  $: selectedLabel = (() => {
    if (!value || value === '') return placeholder;
    const selectedOption = options.find(opt => String(opt.value) === String(value));
    return selectedOption ? selectedOption.label : placeholder;
  })();
  
  
  // Filter options when search text changes or when options change
  $: {
    if (searchText && searchText.length >= 2) {
      // Filter only if 2 or more characters are typed
      const searchLower = searchText.toLowerCase();
      filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchLower)
      );
    } else {
      // If search is less than 2 chars, show all options
      filteredOptions = [...options];
    }
  }
  
  // Toggle dropdown
  function toggleDropdown() {
    isOpen = !isOpen;
    if (isOpen) {
      // Reset search when opening
      searchText = '';
      filteredOptions = [...options];
      // Prevent scrolling on body
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
    }
  }
  
  // Close dropdown
  function closeDropdown() {
    isOpen = false;
    document.body.style.overflow = '';
  }
  
  // Handle option selection
  function selectOption(option) {
    value = option.value;
    closeDropdown();
    dispatch('change', { value: option.value, option });
  }
  
  // Handle clicks outside to close dropdown
  function handleClickOutside(event) {
    if (isOpen && !event.target.closest('.searchable-select-container')) {
      closeDropdown();
    }
  }
  
  // Set up click outside handling
  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });
</script>

<div class="searchable-select-container">
  <!-- Trigger button showing current selection -->
  <button
    type="button"
    class="select-trigger"
    on:click|stopPropagation={toggleDropdown}
    aria-haspopup="listbox"
    aria-expanded={isOpen}
    {id}
  >
  <div>
    {#if selectedImage}
      <img src={selectedImage} alt={selectedLabel} class="selected-image" width="20" height="20" />
    {/if}
    <span class="selected-value" class:is-placeholder={!value}>{selectedLabel}</span>
  </div>
    <span class="dropdown-arrow">▼</span>
  </button>
  

  <!-- Dropdown overlay -->
  {#if isOpen}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="dropdown-overlay" on:click={closeDropdown}>
      <div class="dropdown-modal" on:click|stopPropagation>
        <div class="dropdown-header">
          <h3>{placeholder}</h3>
          <button class="close-button" on:click={closeDropdown}>×</button>
        </div>
        
        <!-- Search input -->
        <div class="search-container">
          <input
            type="text"
            class="search-input"
            placeholder={$_('searchable_select.search')}
            bind:value={searchText}
            autofocus
          />
        </div>
        
        <!-- Options list -->
        <div class="dropdown-list">
          {#if filteredOptions.length === 0}
            <div class="dropdown-empty">
              {searchText.length >= 2
                ? $_('searchable_select.no_results')
                : $_('searchable_select.no_options')}
            </div>
          {:else}
            {#each filteredOptions as option}
              <button
                type="button"
                class="dropdown-item {String(value) === String(option.value) ? 'selected' : ''}"
                on:click={() => selectOption(option)}
              >
                {#if option.image}
                  <img src={option.image} alt={option.label} class="selected-image" width="40" height="40" />
                {/if}
                {option.label}
              </button>
            {/each}
          {/if}
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .searchable-select-container {
    position: relative;
    width: 100%;
  }
  
  .select-trigger {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.75rem 1rem;
    background-color: var(--pico-form-element-background-color);
    border: none;
    border-bottom: 1px solid var(--pico-form-element-border-color);
    border-radius: 0px;
    color: var(--pico-form-element-color);
    cursor: pointer;
    text-align: left;
    font-size: 1rem;
  }
  
  .dropdown-arrow {
    font-size: 0.75rem;
  }
  
  .selected-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selected-value.is-placeholder {
    color: var(--pico-form-element-placeholder-color);
    opacity: 0.7;
  }
  
  /* Full screen modal styles */
  .dropdown-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
  }
  
  .dropdown-modal {
    background-color: var(--color-bg-darker, #17181c);
    width: 100%;
    height: 100%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  
  .dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid var(--color-border, #334155);
  }
  
  .dropdown-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text, white);
  }
  
  .search-container {
    padding: 1rem;
    border-bottom: 1px solid var(--color-border, #334155);
  }
  
  .search-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--color-border, #334155);
    border-radius: var(--border-radius, 0.375rem);
    background-color: var(--color-bg-input, #1e1f25);
    color: var(--color-text, white);
    font-size: 1rem;
  }
    
  .close-button {
    background: transparent;
    border: none;
    color: var(--color-text, white);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
  }
  
  .close-button:hover {
    color: var(--color-text, white);
  }
  
  .dropdown-list {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
  }
  
  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 1rem;
    margin-bottom: 0.5rem;
    background-color: var(--color-bg-input, #17181c);
    border: 1px solid var(--color-border, #334155);
    border-radius: var(--border-radius, 0.375rem);
    color: var(--color-text, white);
    text-align: left;
    font-size: 1.125rem;
    transition: all 0.15s ease;
    cursor: pointer;
  }

  .dropdown-item .selected-image {
    flex-shrink: 0;
    object-fit: contain;
    background-color: white;
    padding: 0.25rem;
    border-radius: 0.25rem;
  }
  
  .dropdown-item:hover {
    background-color: var(--color-primary-light, rgba(59, 130, 246, 0.1));
    color: var(--color-primary);
  }
  
  .dropdown-item.selected {
    background-color: var(--color-primary, #3b82f6);
    color: white;
    border-color: var(--color-primary, #3b82f6);
  }
  
  .dropdown-empty {
    padding: 2rem;
    text-align: center;
    font-style: italic;
  }
  
</style> 